import {
    Button, FormControl, FormHelperText, IconButton,
    InputLabel, MenuItem, Select, Stack, TextField,
    Delete
} from '@leap4/frontend';
import React from 'react';

import type { ReportConstraintOperator } from '../report.interface';

interface Constraint {
    id: string;
    metric: string;
    operator: ReportConstraintOperator;
    value: number | '';
}

interface ConstraintsProps {
    /** Currently selected metrics, keyed by projected column key. */
    metricOptions: { key: string; value: string }[];
    constraintsData: Constraint[];
    onChange: (id: string, field: string, value: any) => void;
    onAddConstraint: () => void;
    onDeleteConstraint: (id: string) => void;
}

const operatorOptions: { key: ReportConstraintOperator; value: string }[] = [
    { key: 'gt', value: '>' },
    { key: 'gte', value: '≥' },
    { key: 'lt', value: '<' },
    { key: 'lte', value: '≤' },
    { key: 'eq', value: '=' },
    { key: 'neq', value: '≠' },
];

interface ConstraintItemProps {
    constraint: Constraint;
    metricOptions: { key: string; value: string }[];
    onChange: (id: string, field: string, value: any) => void;
    onDeleteConstraint: (id: string) => void;
    onAddConstraint: () => void;
    isLast: boolean;
}

const ConstraintItem: React.FC<ConstraintItemProps> = ({
    constraint,
    metricOptions,
    onChange,
    onDeleteConstraint,
    onAddConstraint,
    isLast,
}) => {
    // A metric can be deselected after its constraint was added. Showing it as chosen
    // would send a constraint the report cannot return, which the backend rejects with a
    // 400 — so surface it here instead.
    const metricIsSelected = metricOptions.some(o => o.key === constraint.metric);
    const metricMissing = !!constraint.metric && !metricIsSelected;

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            marginBottom={1}
        >
            <FormControl
                variant="outlined"
                size="small"
                style={{ width: '25%' }}
                error={metricMissing}
            >
                <InputLabel>Metric</InputLabel>
                <Select
                    value={metricIsSelected ? constraint.metric : ''}
                    onChange={(e) => onChange(constraint.id, 'metric', e.target.value)}
                    label="Metric"
                >
                    {metricOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.value}
                        </MenuItem>
                    ))}
                </Select>
                {metricMissing && (
                    <FormHelperText>
                        No longer selected — pick another metric
                    </FormHelperText>
                )}
            </FormControl>

            <FormControl variant="outlined" size="small" style={{ width: '10%' }}>
                <InputLabel>Is</InputLabel>
                <Select
                    value={constraint.operator}
                    onChange={(e) => onChange(constraint.id, 'operator', e.target.value)}
                    label="Is"
                >
                    {operatorOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                size="small"
                type="number"
                style={{ width: '20%' }}
                label="Value"
                value={constraint.value}
                onChange={(e) => {
                    const raw = e.target.value;
                    onChange(constraint.id, 'value', raw === '' ? '' : Number(raw));
                }}
            />

            <IconButton
                color="error"
                onClick={() => onDeleteConstraint(constraint.id)}
                aria-label="delete"
            >
                <Delete />
            </IconButton>

            {isLast && (
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onAddConstraint}
                    style={{ marginLeft: '8px' }}
                >
                    + Add Constraint
                </Button>
            )}
        </Stack>
    );
};

const Constraints: React.FC<ConstraintsProps> = ({
    metricOptions,
    constraintsData,
    onChange,
    onAddConstraint,
    onDeleteConstraint,
}) => {
    return (
        <div>
            <FormHelperText>
                Constraints — drop rows whose metric fails the comparison. Applied after
                totals are grouped, so totals count only the rows shown. Rows with no
                value for the metric are excluded.
            </FormHelperText>
            {constraintsData.map((constraint, index) => (
                <ConstraintItem
                    key={constraint.id}
                    constraint={constraint}
                    metricOptions={metricOptions}
                    onChange={onChange}
                    onDeleteConstraint={onDeleteConstraint}
                    onAddConstraint={onAddConstraint}
                    isLast={index === constraintsData.length - 1}
                />
            ))}
        </div>
    );
};

export default Constraints;
